export class AlternatingProductRailController {
  constructor({ totalItems = 0, rows = 1, visibleColumns = 1, index = 0 } = {}) {
    this.configure({ totalItems, rows, visibleColumns, index });
  }

  configure({
    totalItems = this.totalItems || 0,
    rows = this.rows || 1,
    visibleColumns = this.visibleColumns || 1,
    index = this.index || 0,
  } = {}) {
    this.totalItems = Math.max(0, Math.trunc(Number(totalItems) || 0));
    this.rows = Math.max(1, Math.trunc(Number(rows) || 1));
    this.visibleColumns = Math.max(1, Math.trunc(Number(visibleColumns) || 1));
    this.columns = this.totalItems ? Math.ceil(this.totalItems / this.rows) : 0;
    this.maxIndex = Math.max(0, this.columns - this.visibleColumns);
    this.index = 0;
    this.goTo(index);
    return this.index;
  }

  goTo(index) {
    this.index = Math.min(this.maxIndex, Math.max(0, Math.trunc(Number(index) || 0)));
    return this.index;
  }

  next() {
    return this.goTo(this.index + 1);
  }

  previous() {
    return this.goTo(this.index - 1);
  }

  atStart() {
    return this.index <= 0;
  }

  atEnd() {
    return this.maxIndex === 0 || this.index >= this.maxIndex;
  }
}

export class AlternatingProductGalleryController {
  constructor({ count = 0, index = 0 } = {}) {
    this.count = Math.min(5, Math.max(0, Math.trunc(Number(count) || 0)));
    this.index = 0;
    this.goTo(index);
  }

  goTo(index) {
    if (!this.count) {
      this.index = 0;
      return this.index;
    }
    this.index = Math.min(this.count - 1, Math.max(0, Math.trunc(Number(index) || 0)));
    return this.index;
  }

  next() {
    return this.goTo(this.index + 1);
  }

  previous() {
    return this.goTo(this.index - 1);
  }

  atStart() {
    return this.index <= 0;
  }

  atEnd() {
    return this.count <= 1 || this.index >= this.count - 1;
  }
}

export class LSAlternatingProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.track = this.querySelector('[data-ls-alternating-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-ls-alternating-gallery-slide]')].slice(0, 5);
    this.dots = [...this.querySelectorAll('[data-ls-alternating-gallery-dot]')].slice(0, 5);
    this.previousButton = this.querySelector('[data-ls-alternating-gallery-previous]');
    this.nextButton = this.querySelector('[data-ls-alternating-gallery-next]');
    this.liveRegion = this.querySelector('[data-ls-alternating-gallery-live]');
    this.motionQuery = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
    this.abortController = new AbortController();
    this.scrollFrame = null;
    const { signal } = this.abortController;

    this.controller = new AlternatingProductGalleryController({
      count: this.slides.length,
      index: Number(this.dataset.currentIndex) || 0,
    });

    this.previousButton?.addEventListener('click', () => this.navigate(this.controller.previous()), { signal });
    this.nextButton?.addEventListener('click', () => this.navigate(this.controller.next()), { signal });
    this.track?.addEventListener('scroll', () => this.onScroll(), { signal, passive: true });
    this.track?.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.resizeObserver = globalThis.ResizeObserver
      ? new ResizeObserver(() => this.navigate(this.controller.index, { announce: false, behavior: 'auto' }))
      : null;
    if (this.track) this.resizeObserver?.observe(this.track);

    this.setAttribute('data-ready', '');
    this.syncUi({ announce: false });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    if (this.scrollFrame !== null) globalThis.cancelAnimationFrame?.(this.scrollFrame);
    this.controller = null;
  }

  navigate(index, { announce = true, behavior = 'smooth' } = {}) {
    if (!this.controller) return;
    this.controller.goTo(index);
    const target = this.slides[this.controller.index];
    if (this.track && target) {
      this.track.scrollTo({
        left: target.offsetLeft,
        behavior: this.motionQuery?.matches ? 'auto' : behavior,
      });
    }
    this.syncUi({ announce });
  }

  onScroll() {
    if (!this.track || this.slides.length < 2) return;
    if (this.scrollFrame !== null) globalThis.cancelAnimationFrame?.(this.scrollFrame);
    this.scrollFrame = globalThis.requestAnimationFrame?.(() => {
      this.scrollFrame = null;
      const currentLeft = this.track.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;
      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - currentLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });
      this.controller.goTo(nearestIndex);
      this.syncUi({ announce: false });
    }) ?? null;
  }

  onKeydown(event) {
    if (!this.controller || this.controller.count < 2) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigate(this.controller.previous());
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigate(this.controller.next());
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.navigate(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.navigate(this.controller.count - 1);
    }
  }

  syncUi({ announce = true } = {}) {
    if (!this.controller) return;
    this.dataset.currentIndex = String(this.controller.index);
    this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.controller.index)));
    if (this.previousButton) {
      this.previousButton.hidden = this.controller.count < 2 || this.controller.atStart();
      this.previousButton.disabled = this.controller.atStart();
    }
    if (this.nextButton) {
      this.nextButton.hidden = this.controller.count < 2 || this.controller.atEnd();
      this.nextButton.disabled = this.controller.atEnd();
    }
    if (announce && this.liveRegion) {
      this.liveRegion.textContent = `Product image ${this.controller.index + 1} of ${this.controller.count}`;
    }
  }
}

export class LSAlternatingProductRail extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.track = this.querySelector('[data-ls-alternating-track]');
    this.items = [...this.querySelectorAll('[data-ls-alternating-item]')];
    this.previousButton = this.querySelector('[data-ls-alternating-previous]');
    this.nextButton = this.querySelector('[data-ls-alternating-next]');
    this.liveRegion = this.querySelector('[data-ls-alternating-live]');
    this.layoutQuery = globalThis.matchMedia?.('(max-width: 749px)');
    this.motionQuery = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
    this.abortController = new AbortController();
    this.scrollFrame = null;
    const { signal } = this.abortController;

    this.controller = new AlternatingProductRailController({ totalItems: this.items.length });
    this.previousButton?.addEventListener('click', () => this.navigate(this.controller.previous()), { signal });
    this.nextButton?.addEventListener('click', () => this.navigate(this.controller.next()), { signal });
    this.track?.addEventListener('scroll', () => this.onScroll(), { signal, passive: true });
    this.track?.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.layoutQuery?.addEventListener('change', () => this.configureLayout(), { signal });
    this.resizeObserver = globalThis.ResizeObserver
      ? new ResizeObserver(() => this.navigate(this.controller.index, { announce: false, behavior: 'auto' }))
      : null;
    if (this.track) this.resizeObserver?.observe(this.track);

    this.setAttribute('data-ready', '');
    this.configureLayout();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    if (this.scrollFrame !== null) globalThis.cancelAnimationFrame?.(this.scrollFrame);
    this.controller = null;
  }

  configureLayout() {
    if (!this.controller) return;
    const mobile = this.layoutQuery?.matches || false;
    this.dataset.layout = mobile ? 'mobile' : 'desktop';
    this.controller.configure({
      totalItems: this.items.length,
      rows: mobile ? 1 : 2,
      visibleColumns: 2,
      index: this.controller.index,
    });
    this.navigate(this.controller.index, { announce: false, behavior: 'auto' });
  }

  columnAnchors() {
    const anchors = [];
    this.items.forEach((item) => {
      const left = item.offsetLeft;
      if (!anchors.some((anchor) => Math.abs(anchor - left) < 1)) anchors.push(left);
    });
    return anchors.sort((a, b) => a - b);
  }

  navigate(index, { announce = true, behavior = 'smooth' } = {}) {
    if (!this.controller || !this.track) return;
    this.controller.goTo(index);
    const anchors = this.columnAnchors();
    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const target = Math.min(anchors[this.controller.index] || 0, maxScroll);
    this.track.scrollTo({
      left: target,
      behavior: this.motionQuery?.matches ? 'auto' : behavior,
    });
    this.syncUi({ announce });
  }

  onScroll() {
    if (!this.track || !this.controller || this.controller.maxIndex === 0) return;
    if (this.scrollFrame !== null) globalThis.cancelAnimationFrame?.(this.scrollFrame);
    this.scrollFrame = globalThis.requestAnimationFrame?.(() => {
      this.scrollFrame = null;
      const currentLeft = this.track.scrollLeft;
      const anchors = this.columnAnchors().slice(0, this.controller.maxIndex + 1);
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;
      anchors.forEach((anchor, index) => {
        const distance = Math.abs(anchor - currentLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });
      this.controller.goTo(nearestIndex);
      this.syncUi({ announce: false });
    }) ?? null;
  }

  onKeydown(event) {
    if (event.target !== this.track || !this.controller || this.controller.maxIndex === 0) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigate(this.controller.previous());
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigate(this.controller.next());
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.navigate(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.navigate(this.controller.maxIndex);
    }
  }

  syncUi({ announce = true } = {}) {
    if (!this.controller) return;
    this.dataset.currentIndex = String(this.controller.index);
    if (this.previousButton) {
      this.previousButton.hidden = this.controller.atStart();
      this.previousButton.disabled = this.controller.atStart();
    }
    if (this.nextButton) {
      this.nextButton.hidden = this.controller.atEnd();
      this.nextButton.disabled = this.controller.atEnd();
    }
    if (announce && this.liveRegion && this.controller.totalItems) {
      const start = this.controller.index * this.controller.rows + 1;
      const end = Math.min(
        this.controller.totalItems,
        (this.controller.index + this.controller.visibleColumns) * this.controller.rows,
      );
      this.liveRegion.textContent = `Showing products ${start}–${end} of ${this.controller.totalItems}`;
    }
  }
}

if (!customElements.get('ls-alternating-product-rail')) {
  customElements.define('ls-alternating-product-rail', LSAlternatingProductRail);
}

if (!customElements.get('ls-alternating-product-gallery')) {
  customElements.define('ls-alternating-product-gallery', LSAlternatingProductGallery);
}
